import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Form } from '@/components/ui/form';
import { useToast } from '@/hooks/use-toast';
import { useParams } from 'react-router';
import { IntegrationV2Query, IntegrationTypeV2, TikTokCredentialsModel } from '@/gql/graphql';
import { getEventsList, getUserSelectedEvents } from '@/routes/trackings/tracking-utils';
import { useEffect } from 'react';
import { SaveButton } from '../../components/save-button';
import {
  FormLayout,
  FormSection,
  PasswordField,
  InputField
} from './../../components/tracking-details-form/base';
import {
  ConsentModeSection,
  FormHeaderSection,
  CustomEventsSection
} from '../../components/tracking-details-form/shared-sections';
import { useIntegrationStore } from '../../stores/tracking-store';
import { useApolloClient } from '@apollo/client';

const formSchema = z.object({
  credentials: z.object({
    tiktokId: z.string().min(1, 'Pixel or App ID is required.'),
    accessToken: z.string().min(1, 'CAPI Access Token is required.')
  }),
  consentMode: z.boolean(),
  customEvents: z.array(z.string()).optional()
});

const eventsList = getEventsList(IntegrationTypeV2.Tiktok);

export const TiktokSettingsForm = ({ onContinue }: { onContinue: () => void }) => {
  const { toast } = useToast();
  const params = useParams<{ id: string; slug: string }>();
  const { integration, isLoading, updateSettings } = useIntegrationStore();
  const client = useApolloClient();

  const isInitialSetup =
    !(integration?.credentials as TikTokCredentialsModel | undefined)?.tiktokId &&
    !(integration?.credentials as TikTokCredentialsModel | undefined)?.accessToken;

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      credentials: {
        tiktokId: '',
        accessToken: ''
      },
      consentMode: false,
      customEvents: []
    }
  });

  useEffect(() => {
    if (!isLoading && integration) {
      const credentials = integration.credentials as TikTokCredentialsModel | undefined;

      form.reset({
        credentials: {
          tiktokId: credentials?.tiktokId ?? '',
          accessToken: credentials?.accessToken ?? ''
        },
        consentMode: integration.consentMode ?? false,
        customEvents: getUserSelectedEvents({
          integrationV2: integration as IntegrationV2Query['integrationV2']
        })
      });
    }
  }, [integration, form.reset]);

  const { isDirty } = form.formState;

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    try {
      await updateSettings(client, {
        id: params.id!,
        credentials: values.credentials,
        customEvents: values.customEvents ?? [],
        consentMode: values.consentMode
      });

      toast({
        title: 'Form submitted',
        description: 'Your Tiktok integration settings have been saved.',
        variant: 'default'
      });
      onContinue();
    } catch (_error) {
      toast({
        title: 'Error',
        description: 'Failed to save Tiktok integration settings.',
        variant: 'destructive'
      });
    }
  };

  return (
    <Form {...form}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void form.handleSubmit(onSubmit)(e);
        }}
      >
        <FormLayout>
          <FormHeaderSection title="Tiktok Settings">
            In order to send transactions data to this destination, we need the following
            information from your Tiktok account.
          </FormHeaderSection>
          <FormSection columns={2}>
            <PasswordField form={form} name="credentials.accessToken" label="Tiktok Access Token" />
            <InputField
              form={form}
              name="credentials.tiktokId"
              label="Tiktok Pixel ID"
              placeholder="e.g. 123456789012345"
            />
          </FormSection>
          <ConsentModeSection form={form} />
          <CustomEventsSection
            form={form}
            eventsList={eventsList}
            getUserSelectedEvents={() =>
              getUserSelectedEvents({
                integrationV2: integration as IntegrationV2Query['integrationV2']
              })
            }
          />
          <FormSection>
            <SaveButton isDirty={isDirty} isUpdating={isLoading} isInitialSetup={isInitialSetup} />
          </FormSection>
        </FormLayout>
      </form>
    </Form>
  );
};
